import React, { useState, useEffect } from 'react';
import './style.css';

export const SuperAdmin = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 50
  });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    sortBy: 'created_at',
    order: 'DESC'
  });

  const handleLogout = () => {
    sessionStorage.removeItem('superAdminAuth');
    window.location.reload();
  };

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await fetch(`https://ca-services-pcyc.onrender.com/api/contacts/getValue?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setSubmissions(data.data);
        setPagination(data.pagination);
      } else {
        setError(data.message || 'Failed to fetch submissions');
      }
    } catch (err) {
      setError('Failed to connect to server. Make sure the backend is running on port 5000.');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      order: prev.sortBy === field && prev.order === 'DESC' ? 'ASC' : 'DESC',
      page: 1
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (id) => {
    // Show prompt to select who is deleting
    const deleters = ['ANKITA', 'HARSHITA', 'NIRAJ'];
    let selectedDeleter = null;
    
    // Create custom prompt
    const selection = window.prompt(
      'Who is deleting this entry?\n\nEnter one of:\n- ANKITA\n- HARSHITA\n- NIRAJ',
      ''
    );
    
    if (!selection) {
      return; // User cancelled
    }
    
    selectedDeleter = selection.toUpperCase().trim();
    
    if (!deleters.includes(selectedDeleter)) {
      alert('❌ Invalid selection. Please enter ANKITA, HARSHITA, or NIRAJ');
      return;
    }
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete this submission?\n\nDeleted by: ${selectedDeleter}`)) {
      return;
    }
    // value added
    console.log('api is changed');
    try {
      const response = await fetch(`https://ca-services-pcyc.onrender.com/api/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ deletedBy: selectedDeleter })
      });
      const data = await response.json();

      if (data.success) {
        alert('✅ Submission deleted successfully');
        // Update local state with the deleted record
        setSubmissions(prev => 
          prev.map(sub => sub.id === id ? { ...sub, ...data.data } : sub)
        );
      } else {
        alert('❌ Failed to delete: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('❌ Network error. Please try again.');
    }
  };

  const handleUpdateField = async (id, field, value) => {
    try {
      const body = {};
      body[field] = value;

      const response = await fetch(`https://ca-services-pcyc.onrender.com/api/contacts/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        // Update local state
        setSubmissions(prev => 
          prev.map(sub => sub.id === id ? { ...sub, ...data.data } : sub)
        );
      } else {
        alert('❌ Failed to update: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      alert('❌ Network error. Please try again.');
    }
  };

  const exportToCSV = () => {
    if (submissions.length === 0) return;

    const headers = ['ID', 'Full Name', 'Phone Number', 'Email', 'Service', 'Source', 'Called by Harshit', 'Called by Ankita', 'Client Status', 'Deleted By', 'Created At'];
    const csvData = submissions.map(sub => [
      sub.id,
      sub.fullName,
      sub.phoneNumber,
      sub.email,
      sub.service,
      sub.source,
      sub.calledByHarshit ? 'YES' : 'NO',
      sub.calledByAnkita ? 'YES' : 'NO',
      sub.clientStatus || 'pending',
      sub.isDeleted ? sub.deletedBy : '-',
      formatDate(sub.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="super-admin-container">
      <div className="admin-header">
        <div>
          <h1>Super Admin Dashboard</h1>
          <p>Contact Form Submissions</p>
        </div>
        <button onClick={handleLogout} className="logout-btn">
          🚪 Logout
        </button>
      </div>

      <div className="admin-controls">
        <div className="admin-stats">
          <div className="stat-card">
            <span className="stat-label">Total Submissions</span>
            <span className="stat-value">{pagination.totalRecords}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Current Page</span>
            <span className="stat-value">{pagination.currentPage} / {pagination.totalPages}</span>
          </div>
        </div>
        
        <div className="admin-actions">
          <button onClick={fetchSubmissions} className="refresh-btn">
            🔄 Refresh
          </button>
          <button onClick={exportToCSV} className="export-btn" disabled={submissions.length === 0}>
            📥 Export CSV
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading submissions...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>❌ {error}</p>
          <button onClick={fetchSubmissions} className="retry-btn">Try Again</button>
        </div>
      )}

      {!loading && !error && submissions.length === 0 && (
        <div className="empty-state">
          <p>📭 No submissions found</p>
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID {filters.sortBy === 'id' && (filters.order === 'DESC' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('full_name')} className="sortable">
                    Full Name {filters.sortBy === 'full_name' && (filters.order === 'DESC' ? '↓' : '↑')}
                  </th>
                  <th>Phone Number</th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    Email {filters.sortBy === 'email' && (filters.order === 'DESC' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('service')} className="sortable">
                    Service {filters.sortBy === 'service' && (filters.order === 'DESC' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('source')} className="sortable">
                    Source {filters.sortBy === 'source' && (filters.order === 'DESC' ? '↓' : '↑')}
                  </th>
                  <th>Called by Harshit</th>
                  <th>Called by Ankita</th>
                  <th>Client Status</th>
                  <th>Deleted By</th>
                  <th onClick={() => handleSort('created_at')} className="sortable">
                    Submitted At {filters.sortBy === 'created_at' && (filters.order === 'DESC' ? '↓' : '↑')}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr key={submission.id} className={submission.isDeleted ? 'deleted-row' : ''}>
                    <td>{submission.id}</td>
                    <td>{submission.fullName}</td>
                    <td>{submission.phoneNumber}</td>
                    <td>{submission.email}</td>
                    <td className="service-cell" title={submission.service}>
                      {submission.service}
                    </td>
                    <td>
                      <span className={`source-badge ${submission.source}`}>
                        {submission.source === 'chatbot' ? '💬 Chatbot' : '📝 Form'}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`toggle-btn ${submission.calledByHarshit ? 'yes' : 'no'}`}
                        onClick={() => handleUpdateField(submission.id, 'calledByHarshit', !submission.calledByHarshit)}
                        disabled={submission.isDeleted}
                      >
                        {submission.calledByHarshit ? '✅ YES' : '❌ NO'}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`toggle-btn ${submission.calledByAnkita ? 'yes' : 'no'}`}
                        onClick={() => handleUpdateField(submission.id, 'calledByAnkita', !submission.calledByAnkita)}
                        disabled={submission.isDeleted}
                      >
                        {submission.calledByAnkita ? '✅ YES' : '❌ NO'}
                      </button>
                    </td>
                    <td>
                      <select
                        className={`status-select status-${submission.clientStatus || 'pending'}`}
                        value={submission.clientStatus || 'pending'}
                        onChange={(e) => handleUpdateField(submission.id, 'clientStatus', e.target.value)}
                        disabled={submission.isDeleted}
                      >
                        <option value="pending">⏳ Pending</option>
                        <option value="accepted">✅ Accepted</option>
                        <option value="rejected">❌ Rejected</option>
                      </select>
                    </td>
                    <td>
                      {submission.isDeleted ? (
                        <span className="deleted-badge">
                          🗑️ {submission.deletedBy}
                        </span>
                      ) : (
                        <span className="not-deleted">—</span>
                      )}
                    </td>
                    <td>{formatDate(submission.createdAt)}</td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(submission.id)}
                        title="Delete submission"
                        disabled={submission.isDeleted}
                      >
                        {submission.isDeleted ? '🚫 Deleted' : '🗑️ Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button 
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1}
              className="page-btn"
            >
              ⏮️ First
            </button>
            <button 
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="page-btn"
            >
              ⬅️ Previous
            </button>
            
            <span className="page-info">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <button 
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="page-btn"
            >
              Next ➡️
            </button>
            <button 
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="page-btn"
            >
              Last ⏭️
            </button>
          </div>
        </>
      )}
    </div>
  );
};

